import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient } from '../../../api/client';
import {
  TEXT_DARK, TEXT_MUTED, FONT_HEADING,
  BG_SURFACE, BORDER_LIGHT,
  STATUS_COLORS,
} from './designTokens';

export interface InventoryData {
  total: number;
  assigned: number;
  available: number;
  held: number;
  blocked: number;
  checkedIn: number;
  warningThreshold: number;
}

const styles: Record<string, React.CSSProperties> = {
  bar: {
    display: 'flex',
    alignItems: 'center',
    gap: '1.25rem',
    padding: '0.75rem 1rem',
    backgroundColor: BG_SURFACE,
    borderRadius: '6px',
    border: `1px solid ${BORDER_LIGHT}`,
    marginBottom: '1rem',
    flexWrap: 'wrap',
  },
  stat: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minWidth: '70px',
  },
  statValue: {
    fontSize: '1.1rem',
    fontFamily: FONT_HEADING,
    fontWeight: 700,
    color: TEXT_DARK,
  },
  statLabel: {
    fontSize: '0.7rem',
    color: TEXT_MUTED,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  divider: {
    width: '1px',
    height: '32px',
    backgroundColor: BORDER_LIGHT,
  },
  timestamp: {
    fontSize: '0.75rem',
    color: TEXT_MUTED,
    marginLeft: 'auto',
  },
  warning: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.35rem',
    padding: '0.25rem 0.6rem',
    backgroundColor: '#fff8e1',
    border: '1px solid #ffa000',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#e65100',
  },
  stale: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.35rem',
    padding: '0.25rem 0.6rem',
    backgroundColor: '#fdecea',
    border: '1px solid #ef9a9a',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#b71c1c',
  },
};

export function InventorySummary({ refreshKey }: { refreshKey?: number }) {
  const [inventory, setInventory] = useState<InventoryData | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [isStale, setIsStale] = useState(false);
  const [loading, setLoading] = useState(true);
  const lastFetchRef = useRef<number>(0);

  const fetchInventory = useCallback(async () => {
    try {
      const data = await apiClient<any>('/table-management/inventory');
      // API returns { overall: {...}, byZone: [...], warningActive, warningThreshold }
      const overall = data.overall || data;
      setInventory({
        total: overall.total ?? 0,
        assigned: overall.assigned ?? 0,
        available: overall.available ?? 0,
        held: overall.held ?? 0,
        blocked: overall.blocked ?? 0,
        checkedIn: overall.checkedIn ?? 0,
        warningThreshold: data.warningThreshold ?? 90,
      });
      setLastUpdated(new Date());
      setIsStale(false);
      lastFetchRef.current = Date.now();
    } catch {
      // On failure, mark data as stale but retain last counts
      setIsStale(true);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);

  // Auto-refresh on browser focus return (within 3 seconds)
  useEffect(() => {
    function handleFocus() {
      const timeSinceLastFetch = Date.now() - lastFetchRef.current;
      // Only refresh if more than 3 seconds since last fetch
      if (timeSinceLastFetch > 3000) {
        fetchInventory();
      }
    }

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [fetchInventory]);

  // Re-fetch when refreshKey changes (triggered by mutations elsewhere)
  useEffect(() => {
    if (refreshKey && refreshKey > 0) {
      fetchInventory();
    }
  }, [refreshKey, fetchInventory]);

  if (loading && !inventory) {
    return (
      <div style={styles.bar}>
        <span style={{ color: '#666', fontSize: '0.85rem' }}>Loading inventory...</span>
      </div>
    );
  }

  if (!inventory) {
    return (
      <div style={{ ...styles.bar, ...styles.stale }}>
        <span>Unable to load seat inventory.</span>
      </div>
    );
  }

  const usedPercentage = inventory.total > 0
    ? ((inventory.assigned + inventory.held) / inventory.total) * 100
    : 0;
  const isOverThreshold = usedPercentage >= inventory.warningThreshold;

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  return (
    <div style={styles.bar} role="region" aria-label="Seat inventory summary">
      <div style={styles.stat}>
        <span style={styles.statValue}>{inventory.total}</span>
        <span style={styles.statLabel}>Total</span>
      </div>
      <div style={styles.divider} />
      <div style={styles.stat}>
        <span style={{ ...styles.statValue, color: STATUS_COLORS.Assigned }}>{inventory.assigned}</span>
        <span style={styles.statLabel}>Assigned</span>
      </div>
      <div style={styles.divider} />
      <div style={styles.stat}>
        <span style={{ ...styles.statValue, color: STATUS_COLORS.Available }}>{inventory.available}</span>
        <span style={styles.statLabel}>Available</span>
      </div>
      <div style={styles.divider} />
      <div style={styles.stat}>
        <span style={{ ...styles.statValue, color: STATUS_COLORS.Held }}>{inventory.held}</span>
        <span style={styles.statLabel}>Held</span>
      </div>
      <div style={styles.divider} />
      <div style={styles.stat}>
        <span style={{ ...styles.statValue, color: STATUS_COLORS.Blocked }}>{inventory.blocked}</span>
        <span style={styles.statLabel}>Blocked</span>
      </div>
      <div style={styles.divider} />
      <div style={styles.stat}>
        <span style={{ ...styles.statValue, color: STATUS_COLORS.CheckedIn }}>{inventory.checkedIn}</span>
        <span style={styles.statLabel}>Checked In</span>
      </div>

      {isOverThreshold && (
        <div style={styles.warning} role="alert">
          ⚠️ Capacity {usedPercentage.toFixed(0)}% ({inventory.warningThreshold}% threshold)
        </div>
      )}

      {isStale && (
        <div style={styles.stale} role="alert" aria-live="polite">
          ⚠ Data may be stale
        </div>
      )}

      <span style={styles.timestamp}>
        {lastUpdated ? `Updated ${formatTime(lastUpdated)}` : ''}
      </span>
    </div>
  );
}
